import {BreakpointObserver} from '@angular/cdk/layout';
import {Component, inject, OnDestroy, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {filter, Subscription} from 'rxjs';
import {MatSidenav} from '@angular/material/sidenav';
import {NavigationEnd, Router, RouterModule} from '@angular/router';
import {navItems} from './sidebar/sidebar-data';
import {AppNavItemComponent} from './sidebar/nav-item/nav-item.component';
import {MaterialModule} from 'src/app/material.module';
import {CommonModule} from '@angular/common';
import {SidebarComponent} from './sidebar/sidebar.component';
import {HeaderComponent} from './header/header.component';
import {SidebarService} from "../../services/layout/sidebar.service";
import {ClientSettingsComponent} from "../../components/client-settings/client-settings.component";
import {ApiKeysComponent} from "../../components/client-settings/api-keys/api-keys.component";
import {ClientService} from "../../services/auth/client.service";
import {NavItem} from './sidebar/nav-item/nav-item';
import {
    EnrichReadiness,
    EnrichReadinessDimensionKey,
    EnrichSetupService
} from '../../services/enrich/enrich-setup.service';
import {ProductType as ProductTypeEnum} from '../../models/client-options.model';
import {normalizeProductType, PRODUCT_ORDER, productSlugFromType} from '../../shared/products';

interface ProductNavSection {
    product: ProductTypeEnum;
    items: NavItem[];
    groups: NavGroup[];
}

interface NavGroup {
    title?: string;
    items: NavItem[];
}

const MOBILE_VIEW = 'screen and (max-width: 768px)';
const TABLET_VIEW = 'screen and (min-width: 769px) and (max-width: 1024px)';
const MONITOR_VIEW = 'screen and (min-width: 1024px)';

@Component({
  selector: 'app-full',
  standalone: true,
  imports: [
    RouterModule,
    AppNavItemComponent,
    MaterialModule,
    CommonModule,
    SidebarComponent,
    HeaderComponent,
  ],
  templateUrl: './full.component.html',
  styleUrls: ['./full.component.scss'],
  encapsulation: ViewEncapsulation.None,
})

export class FullComponent implements OnInit, OnDestroy {
    public readonly clientService = inject(ClientService);
    private readonly breakpointObserver = inject(BreakpointObserver);
    private readonly sidebarService = inject(SidebarService);
    private readonly enrichSetupService = inject(EnrichSetupService);
    private readonly router = inject(Router);

  // Define product type constants for use in the template
    ProductType = ProductTypeEnum;

  allNavItems = navItems;
    navSections: ProductNavSection[] = [];
    activeProduct: ProductTypeEnum = ProductTypeEnum.ShineAI;
  currentEnrichStep: number = 1; // Default to step 1
    currentEnrichReadiness: EnrichReadiness = this.enrichSetupService.getCurrentReadiness();
    sectionStates: Record<string, boolean> = {};
    fullWidthContent = false;
    groupStates: Record<string, Record<string, boolean>> = {};

  @ViewChild('leftsidenav')
  public sidenav: MatSidenav | any;

  @ViewChild('rightsidenav')
  public rightSideNav: MatSidenav | any;


  //get options from service
  private layoutChangesSubscription = Subscription.EMPTY;
    private productSubscription = Subscription.EMPTY;
    private readinessSubscription = Subscription.EMPTY;
    private routeSubscription = Subscription.EMPTY;
  private isMobileScreen = false;
  private isContentWidthFixed = true;
  private isCollapsedWidthFixed = false;
  private htmlElement!: HTMLHtmlElement;
    private readonly allProducts = PRODUCT_ORDER;

  get isOver(): boolean {
    return this.isMobileScreen;
  }

    constructor() {

    this.htmlElement = document.querySelector('html')!;
    this.htmlElement.classList.add('light-theme');
    this.layoutChangesSubscription = this.breakpointObserver
      .observe([MOBILE_VIEW, TABLET_VIEW, MONITOR_VIEW])
      .subscribe((state) => {
        // SidenavOpened must be reset true when layout changes

        this.isMobileScreen = state.breakpoints[MOBILE_VIEW];

        this.isContentWidthFixed = state.breakpoints[MONITOR_VIEW];
      });
      // Subscribe to readiness changes so navigation and guards share the same setup model.
    this.readinessSubscription = this.enrichSetupService.readiness$.subscribe(readiness => {
      this.currentEnrichReadiness = readiness;
      this.currentEnrichStep = readiness.currentStep;
        this.updateNavSections();
        this.expandActiveSection();
    });

      // Subscribe to product changes
      this.productSubscription = this.clientService.activeProduct$.subscribe(product => {
          const normalizedProduct = this.normalizeProductName(product);
          this.activeProduct = normalizedProduct;
          this.updateNavSections();
          this.expandActiveSection();

          // If switching to Enrich, check the setup step
          if (normalizedProduct === ProductTypeEnum.ShineEnrich) {
              this.enrichSetupService.checkSetupStatus();
          }
      });
        this.routeSubscription = this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd)
        ).subscribe(() => {
            this.updateFullWidthFlag();
            this.expandActiveSection();
            this.expandActiveGroups();
        });
        this.updateFullWidthFlag();
  }

  ngAfterViewInit(): void {
    this.sidebarService.registerSidenav(this.rightSideNav);
  }

  ngOnInit(): void {
      // Initial check of setup status
    this.enrichSetupService.checkSetupStatus();
      // Ensure sections are built on init
      this.updateNavSections();
      this.expandActiveSection();
  }

  /**
   * Builds visible navigation sections per product based on entitlements and progress
   */
  updateNavSections(): void {
      const enabledProducts = this.clientService.client?.enabledProducts ?? [];
      const normalizedEnabled = enabledProducts.map(product => this.normalizeProductName(product));
      const productsToDisplay: ProductTypeEnum[] = this.sortProducts(
          Array.from(new Set<ProductTypeEnum>([...normalizedEnabled, ...this.allProducts])) as ProductTypeEnum[]
      );

      this.navSections = productsToDisplay
          .map(product => {
              const productNavItems = this.allNavItems.filter(item => this.shouldIncludeItemForProduct(item, product));
              const groups = this.buildGroups(productNavItems);
              const actionableItems = productNavItems.filter(item => !item.navCap);

              return {
                  product,
                  items: actionableItems,
                  groups,
              };
          })
          .filter(section => section.items.length > 0);

      this.ensureSectionStates();
      this.ensureGroupStates();
      this.expandActiveGroups();
  }

  /**
   * Determines if a product section is currently expanded
   */
  isSectionExpanded(product: ProductTypeEnum): boolean {
      return this.sectionStates[product] ?? false;
  }

    /**
     * Resolves an icon name for the given product
     */
    getProductIcon(product: ProductTypeEnum): string {
        switch (product) {
            case this.ProductType.ShineAI:
                return 'sparkles';
            case this.ProductType.ShineTranslations:
                return 'files';
            case this.ProductType.ShineConnect:
                return 'hexagon';
            case this.ProductType.ShineEnrich:
                return 'building';
            case this.ProductType.ShineWebinars:
                return 'presentation-analytics';
            default:
                return 'presentation-analytics';
        }
    }

    /**
     * Marks a section as expanded
     */
    onSectionOpened(product: ProductTypeEnum): void {
        if (this.sectionStates[product]) {
            return;
        }

        Object.keys(this.sectionStates).forEach(section => {
            this.sectionStates[section] = section === product;
        });
        // Collapse all groups when switching sections
        Object.keys(this.groupStates).forEach(prod => {
            this.groupStates[prod] = {};
        });
        this.ensureGroupStates();
  }

    /**
     * Marks a section as collapsed
     */
    onSectionClosed(product: ProductTypeEnum): void {
        if (!this.sectionStates[product]) {
            return;
        }

        this.sectionStates[product] = false;
  }

    ngOnDestroy(): void {
    this.layoutChangesSubscription.unsubscribe();
      this.productSubscription.unsubscribe();
      this.readinessSubscription.unsubscribe();
      this.routeSubscription.unsubscribe();
  }

    /**
     * Determines if a nav item should be visible for a given product
     */
    private shouldIncludeItemForProduct(item: NavItem, product: ProductTypeEnum): boolean {
        const matchesProduct = item.productType === product || item.productType === 'all';
        if (!matchesProduct) {
            return false;
        }

        if (item.productType === this.ProductType.ShineEnrich) {
            if (this.currentEnrichReadiness.localSearchOnly &&
                (item.requiresOperationalReadiness || item.requiredReadiness?.length)) {
                return item.availableInLocalSearch === true;
            }
            if (item.requiresOperationalReadiness) {
                return this.currentEnrichReadiness.isOperationalReady;
            }

            if (item.requiredReadiness?.length) {
                return item.requiredReadiness.every(key => this.isReadinessDimensionReady(key));
            }
        }

        if (item.requiredStep !== undefined && item.productType !== this.ProductType.ShineEnrich) {
            return this.currentEnrichStep >= item.requiredStep;
        }

        return true;
  }

    private isReadinessDimensionReady(key: EnrichReadinessDimensionKey): boolean {
        return this.currentEnrichReadiness.dimensions.some(
            dimension => dimension.key === key && dimension.status === 'ready'
        );
    }

    /**
     * Builds a single nav item that links to the product landing page
     */
    private buildLandingNavItemForProduct(product: ProductTypeEnum): NavItem {
        const icon = this.getLandingIcon(product);
        return {
            displayName: 'Overview',
            iconName: icon,
            route: this.getLandingRoute(product),
            productType: product,
        };
    }

    /**
     * Uses a product-flavored icon for the landing entry
     */
    private getLandingIcon(product: ProductTypeEnum): string {
        switch (product) {
            case ProductTypeEnum.ShineConnect:
                return 'plug';
            case ProductTypeEnum.ShineEnrich:
                return 'hexagon';
            case ProductTypeEnum.ShineWebinars:
                return 'presentation-analytics';
            case ProductTypeEnum.ShineAI:
            default:
                return 'sparkles';
        }
    }

  /**
   * Ensures section state is initialized for available products
   */
  private ensureSectionStates(): void {
      const existingStates = {...this.sectionStates};
      this.sectionStates = {};
      let expandedProduct: string | null = null;

      this.navSections.forEach((section, index) => {
          const wasExpanded = existingStates[section.product] ?? false;

          if (section.product === this.activeProduct) {
              this.sectionStates[section.product] = true;
              expandedProduct = section.product;
          } else if (!expandedProduct && wasExpanded) {
              this.sectionStates[section.product] = true;
              expandedProduct = section.product;
          } else if (!expandedProduct && index === 0) {
              this.sectionStates[section.product] = true;
              expandedProduct = section.product;
          } else {
              this.sectionStates[section.product] = false;
          }
      });
  }

  /**
   * Ensures group expansion state is initialised per product/group
   */
  private ensureGroupStates(): void {
      const updatedStates: Record<string, Record<string, boolean>> = {};

      this.navSections.forEach(section => {
          const productStates = this.groupStates[section.product] ?? {};
          updatedStates[section.product] = {};

          section.groups.forEach((group, index) => {
              const key = this.getGroupKey(section.product, group.title, index);
              const defaultExpanded = group.title ? false : true;
              updatedStates[section.product][key] = productStates[key] ?? defaultExpanded;
          });
      });

      this.groupStates = updatedStates;
  }

  /**
   * Toggles a group within a product section
   */
  toggleGroup(product: string, title: string | undefined, index: number): void {
      const key = this.getGroupKey(product, title, index);
      const productStates = this.groupStates[product] ?? {};
      const defaultExpanded = title ? false : true;
      const currentState = productStates[key] ?? defaultExpanded;
      const nextState = !currentState;

      this.groupStates[product] = {
          ...productStates,
          [key]: nextState,
      };
  }

  /**
   * Determines if a group is expanded; defaults to true
   */
  isGroupExpanded(product: string, title: string | undefined, index: number): boolean {
      const productStates = this.groupStates[product];
      if (!productStates) {
          return title ? false : true;
      }

      const key = this.getGroupKey(product, title, index);
      const state = productStates[key];
      if (state !== undefined) {
          return state;
      }

      return title ? false : true;
  }

  /**
   * Expands the section matching the active product
   */
  private expandActiveSection(): void {
      if (this.sectionStates[this.activeProduct] === undefined) {
          return;
      }

      Object.keys(this.sectionStates).forEach(product => {
          this.sectionStates[product] = product === this.activeProduct;
      });
  }

    /**
     * Expands nav groups that contain the current active route
     */
    private expandActiveGroups(): void {
        const currentRoute = this.router.url;

        this.navSections.forEach(section => {
            section.groups.forEach((group, index) => {
                const hasActiveItem = group.items.some(item => this.isRouteActive(item.route, currentRoute));
                if (hasActiveItem) {
                    const key = this.getGroupKey(section.product, group.title, index);
                    const productStates = this.groupStates[section.product] ?? {};
                    this.groupStates[section.product] = {
                        ...productStates,
                        [key]: true,
                    };
                }
            });
        });
    }

    /**
     * Computes the landing route for a product
     */
    private getLandingRoute(product: ProductTypeEnum): string {
        const slug = productSlugFromType(product);
        return `/products/${slug}/landing`;
    }

    /**
     * Utility to sort products in a consistent order
     */
    private sortProducts(products: ProductTypeEnum[]): ProductTypeEnum[] {
        const productOrder = PRODUCT_ORDER;
        return products
            .slice()
            .sort((a, b) => {
                const indexA = productOrder.indexOf(a);
                const indexB = productOrder.indexOf(b);

                if (indexA === -1 && indexB === -1) {
                    return a.localeCompare(b);
                }
                if (indexA === -1) {
                    return 1;
                }
                if (indexB === -1) {
                    return -1;
                }
                return indexA - indexB;
            });
  }

    private isRouteActive(itemRoute: string | undefined, currentRoute?: string): boolean {
        if (!itemRoute) return false;

        const current = this.normaliseRoute(currentRoute ?? this.router.url);
        const target = this.normaliseRoute(itemRoute);

        if (target === '/webinars') {
            if (current === '/webinars') return true;
            if (current.startsWith('/webinars/')) {
                const segments = current.split('/').filter(Boolean);
                const nextSegment = segments[1] || '';
                return nextSegment.length > 0 && nextSegment !== 'settings';
            }
            return false;
        }

        if (current === target) return true;
        return current.startsWith(`${target}/`);
    }

    private normaliseRoute(route: string): string {
        const [path] = route.split('?');
        const cleanPath = path.split('#')[0];
        return cleanPath.endsWith('/') && cleanPath !== '/' ? cleanPath.slice(0, -1) : cleanPath;
    }

  /**
   * Builds grouped navigation items for a product based on nav captions
   */
  private buildGroups(items: NavItem[]): NavGroup[] {
      const groups: NavGroup[] = [];
      let currentGroup: NavGroup | null = null;

      items.forEach(item => {
          if (item.navCap) {
              currentGroup = {title: item.navCap, items: []};
              groups.push(currentGroup);
              return;
          }

          if (!currentGroup) {
              currentGroup = {items: []};
              groups.push(currentGroup);
          }

          currentGroup.items.push(item);
      });

      return groups.filter(group => group.items.length > 0);
  }

  private getGroupKey(product: string, title: string | undefined, index: number): string {
      return `${product}::${title ?? `group-${index}`}`;
  }

  /**
   * Normalizes product labels from the API to the expected display form
   */
  private normalizeProductName(product: string): ProductTypeEnum {
      return normalizeProductType(product);
  }

  toggleCollapsed() {
    this.isContentWidthFixed = false;
  }

  onSidenavClosedStart() {
    this.isContentWidthFixed = false;
  }

    onSidenavOpenedChange(_isOpened: boolean) {
    this.isCollapsedWidthFixed = !this.isOver;
  }

  openClientSettings() {
    this.sidebarService.open(ClientSettingsComponent, { /* pass data if needed */ });
  }

  openApiKeys() {
    // Assume you have the current client available
    const currentClient = this.clientService.client;
    this.sidebarService.open(ApiKeysComponent, { client: currentClient });
  }

    private updateFullWidthFlag(): void {
        let snapshot = this.router.routerState.snapshot.root;
        while (snapshot.firstChild) {
            snapshot = snapshot.firstChild;
        }
        this.fullWidthContent = !!snapshot.data?.['fullWidth'];
    }
}
